import { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles as allArticles } from '@/mocks/articles';
import { applySeo, getArticlePageSeo, getArticleSchema, getBreadcrumbSchema } from '@/utils/seo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);
  const schemaInjected = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (article) {
      applySeo(getArticlePageSeo(article.title, article.slug, article.excerpt, article.tags));
    }
    if (!schemaInjected.current && article) {
      schemaInjected.current = true;
      const schemas = [
        getArticleSchema({
          title: article.title,
          description: article.excerpt,
          author: article.author,
          datePublished: article.date,
          url: `https://trainology.github.io/article/${article.slug}`,
          imageUrl: article.imageUrl,
        }),
        getBreadcrumbSchema([
          { name: '首页', url: 'https://trainology.github.io' },
          { name: article.categoryName, url: `https://trainology.github.io/category/${article.category}` },
          { name: article.title, url: `https://trainology.github.io/article/${article.slug}` },
        ]),
      ];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, [article]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 pb-14 text-center px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />
      <article className="pt-20 pb-14 md:pb-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: article.categoryName, url: `/category/${article.category}` },
              { name: article.title },
            ]}
          />

          <div className="mt-6">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-md">
              {article.categoryName}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
            {article.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟阅读
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-8 w-full h-64 md:h-80 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="mt-8 prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 cursor-pointer transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              返回 {article.categoryName}
            </Link>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}